import {
  ApiConfiguration,
  Customer,
  CustomerAuthResponse,
  Firm,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  HttpError,
  HttpErrorMessageType
} from '@eo-storefronts/eo-core'
import { useState } from 'react'
import useSetAuthState from '~/src/hooks/auth/useSetAuthState'
import useSetCustomerState from '~/src/hooks/customer/useSetCustomerState'
import usePushNotifications from '~/src/hooks/push-notifications/usePushNotifications'
import { useEoValue } from '~/src/hooks/useEoState'
import { loginAsCustomer } from '~/src/services/AuthenticationService'
import { AuthenticationState } from '~/src/stores/authentication'
import { FIRM_SELECTOR } from '~/src/stores/firm'
import { Credentials } from '~/src/types/Credentials'

interface ReturnsType {
  login(credentials: Credentials): Promise<Customer | null>,
  errors: Record<string, HttpErrorMessageType[]> | string | null,
  loading: boolean,
}

const useLoginAsCustomer = (): ReturnsType => {
  const { initPushNotifications } = usePushNotifications()
  const { setAuthState } = useSetAuthState()
  const { setCustomer } = useSetCustomerState()
  const [ errors, setErrors ] = useState<Record<string, HttpErrorMessageType[]> | string | null>(null)
  const [ loading, setLoading ] = useState<boolean>(false)
  const firm = useEoValue<Firm | null>(FIRM_SELECTOR)

  const login = async (credentials: Credentials): Promise<Customer | null> => {
    setErrors(null)
    setLoading(true)

    try {
      const loginResponse: CustomerAuthResponse = await loginAsCustomer(credentials)

      if (!loginResponse.data.id || !firm?.id || !loginResponse.data.uid || !loginResponse.data?.customerToken) {
        return null
      }

      const authState: Partial<AuthenticationState> = {
        guest: undefined,
        customer: {
          id: loginResponse.data.id,
          uid: loginResponse.data.uid,
          expiresIn: 3600,
          token: loginResponse.data?.customerToken ?? ''
        },
        loggedInAs: 'customer'
      }

      const customerUid: string = loginResponse.data.uid

      await initPushNotifications(customerUid)
      ApiConfiguration.addOrReplaceHeader('customer_token', authState.customer?.token)

      const customer: Customer | null = await setCustomer(authState)

      setAuthState(authState)

      return customer
    } catch (e: any | HttpError) {
      if (e?.errors) {
        setErrors(e.errors)
      } else if (e?.message) {
        setErrors(e.message)
      }

      return null
    } finally {
      setLoading(false)
    }
  }

  return { login, errors, loading }
}

export {
  useLoginAsCustomer
}
